// Decision-direction helpers. The trading code gates BUY/SELL signals through
// a `min_expected_profit_pct` check (default 0.005 in production CDK), so any
// action with magnitude below that threshold would have been dropped and is
// effectively a HOLD even if its sign is non-zero. Mirror that here so the
// dashboard's labels match what the system actually did.
export const MIN_TRADE_ACTION = 0.005;

// Three buckets for visual coloring / leaderboard labels. The decision Lambda
// stores a more granular `signal_type` on each row (BUY / SELL_SHORT /
// SELL_REDUCE / HOLD); `coarseSignal` collapses those into the same three
// buckets the chart cares about.
export type Signal = "BUY" | "SELL" | "HOLD";

// What the decision Lambda actually writes onto each ModelPredictions row.
export type StoredSignal = "BUY" | "SELL_SHORT" | "SELL_REDUCE" | "HOLD";

export function coarseSignal(stored: StoredSignal | undefined | null): Signal {
  if (stored === "BUY") return "BUY";
  if (stored === "SELL_SHORT" || stored === "SELL_REDUCE") return "SELL";
  return "HOLD";
}

// Fallback for rows written before the signal_type field was added: derive
// from the ensemble_action magnitude using the same threshold the trading
// code applies in action_to_orders.
export function classifyAction(action: number | null | undefined): Signal {
  if (action === null || action === undefined || Number.isNaN(action)) return "HOLD";
  if (action > MIN_TRADE_ACTION) return "BUY";
  if (action < -MIN_TRADE_ACTION) return "SELL";
  return "HOLD";
}

// Prefer the stored value; fall back to deriving from action sign+magnitude.
export function signalFor(
  storedSignal: StoredSignal | undefined | null,
  action: number | null | undefined,
): Signal {
  if (storedSignal) return coarseSignal(storedSignal);
  return classifyAction(action);
}

export function signalColor(s: Signal): string {
  if (s === "BUY") return "#22c55e";
  if (s === "SELL") return "#ef4444";
  return "#64748b";
}

// Models written by scripts/backfill_observability.py use this synthetic
// model_id because per-model attribution was never logged at decision time.
// Detect them so the dashboard can flag the limitation to the user.
export function isBackfilledModel(model_id: string | undefined | null): boolean {
  if (!model_id) return false;
  return model_id.startsWith("BACKFILL-");
}
